/**
 * Analytics report taxonomy: groups the report `value`s from getTableList
 * (analytics/reportRegistry.ts) into categories. Drives the indented Analytics
 * children in the sidebar (first entry with permission = category link) and the
 * category tab strip, at both building and portfolio level — one list, so the
 * two navs name and order their categories the same way. Order matters in both
 * dimensions: category order is sidebar order, value order picks the flagship.
 */
export interface AnalyticsCategory {
  slug: string
  label: string
  /** Building-scope reports (served under /b/:bId). */
  values: string[]
  /** Portfolio-scope twins (the `Global*` values under Portfolio Analytics). */
  portfolioValues?: string[]
}

export const ANALYTICS_CATEGORIES: AnalyticsCategory[] = [
  {
    // The Asset main-nav item's dashboards (they render under its tab shell,
    // not the analytics one) — listed here so pickers group them together.
    slug: 'asset',
    label: 'Asset',
    values: ['CapitalPlanning', 'LeaseAbstraction', 'RoofAbstraction'],
    portfolioValues: ['GlobalCapitalPlanning', 'GlobalLeaseAbstraction', 'GlobalRoofAbstraction', 'GlobalComplianceRecords'],
  },
  {
    slug: 'equipment',
    label: 'Equipment',
    values: ['EquipmentDashboard', 'EquipmentLookup', 'EquipmentTrend', 'EquipmentDataIntegrity'],
    portfolioValues: ['GlobalEquipmentDashboard', 'GlobalEquipmentDataIntegrity'],
  },
  {
    slug: 'feedback',
    label: 'Feedback',
    values: [
      'BathroomService',
      'FeedbackDashboard',
      'CustomFeedbackDashboard',
      'ComprehensiveFeedbackReport',
      'InAppFeedbackReport',
      'CustomerFeedbackOnSR',
      'SocialReputationReport',
    ],
    portfolioValues: ['GlobalFeedbackDashboard', 'GlobalSocialReputation', 'GlobalResidentFeedback'],
  },
  {
    slug: 'invoicing',
    label: 'Invoicing',
    values: ['AccountingDashboard', 'collectionDashboard'],
    portfolioValues: ['GlobalAccountingDashboard', 'CollectionDashboardGlobal'],
  },
  {
    slug: 'pmDashboard',
    label: 'Preventative Maintenance',
    values: ['PMCompliance', 'PreventativeMaintenance', 'PMLibraryDashboard'],
    portfolioValues: ['GlobalPMCompliance'],
  },
  {
    slug: 'rounds',
    label: 'Rounds & Inspections',
    values: [
      'Cleaning',
      'CleaningIntelligence',
      'DeficiencyTracker',
      'Engineering',
      'EngineeringIntelligence',
      'SecurityIntelligence',
      // Inspection Overview sits beside Inspection Details: the matrix and the walk detail
      // are the two halves of the same subject.
      'InspectionRound',
      'Inspection',
      'SecurityPatrol',
      'AEDInspection',
      'FireExtinguisherInspection',
    ],
    portfolioValues: [
      // Inspection Overview leads the portfolio strip (and so is the category's flagship
      // link in the sidebar); the rest keep the building order.
      'GlobalInspectionReport',
      'GlobalPortfolioRoundAnalytics',
      'GlobalCleaningIQ',
      'DeficiencyTrackerGlobal',
      'GlobalPortfolioRoundAnalyticsEngineering',
      'GlobalEngineeringIQ',
      'GlobalSecurityIQ',
      'GlobalPortfolioRoundAnalyticsSecurity',
    ],
  },
  {
    // Both values are the live remnants of the retired Access & Compliance
    // category (nav reorg): its other members were disabled or moved to the
    // COI item, leaving these two registered and permitted but uncategorized,
    // so the sidebar could not reach them. Re-homed here.
    slug: 'suppliers',
    label: 'Suppliers',
    values: ['SupplierCheckInReport', 'SupplierSLAReport'],
  },
  {
    slug: 'teamPerformance',
    label: 'Team Performance',
    values: ['TeamPerformance'],
    portfolioValues: ['GlobalTeamPerformance', 'GlobalJobReport', 'GlobalPortfolioMobileEmployee'],
  },
  {
    slug: 'tenant',
    label: 'Tenant',
    values: ['TenantHealthDashboard', 'TenantPerformanceDashboard', 'Renewal', 'MoveOutReport', 'ResidentLog'],
    portfolioValues: ['GlobalTenantHealthDashboard', 'GlobalTenantPerformanceDashboard'],
  },
  {
    slug: 'workOrders',
    label: 'Work Orders',
    values: ['WorkOrderDashboard', 'WorkOrderIntelligence'],
    portfolioValues: ['GlobalWorkOrderDashboard'],
  },
  {
    // Portfolio-only: the building Insurance Dashboard lives under the COI
    // nav item and the Property Characteristics tab under Building Info, so
    // neither has a building-scope category of its own.
    slug: 'insurance',
    label: 'Insurance',
    values: [],
    portfolioValues: ['GlobalInsuranceDashboard'],
  },
  {
    slug: 'propertyCharacteristics',
    label: 'Property Characteristics',
    values: [],
    portfolioValues: ['GlobalPropertyCharacteristics'],
  },
  {
    // Suppressed from the nav entirely (all values disabled) but kept so
    // subscription/report pickers can still label the values by category.
    slug: 'marketing',
    label: 'Marketing & Leads',
    values: ['LeadCapture', 'Vacancy', 'EVRequest', 'RoomRental', 'FieldMarketing', 'EventRegistration'],
    portfolioValues: ['GlobalUpsellMatrix'],
  },
  {
    slug: 'masterNotepad',
    label: 'Master Notepad',
    values: ['MasterNotepad'],
  },
]

/** Every report value in a category, building and portfolio scope together. */
export const categoryValues = (category: AnalyticsCategory): string[] => [
  ...category.values,
  ...(category.portfolioValues || []),
]

export const categoryOfValue = (value: string): AnalyticsCategory | undefined =>
  ANALYTICS_CATEGORIES.find((category) => categoryValues(category).includes(value))

